import React, { useCallback, useState } from 'react';
import { View } from 'react-native';
import Animated from 'react-native-reanimated';
import { AppView, CardView, rem, SCROLL_INDICATOR_INSETS, withAnimatedNavigationHeader, } from 'design-system';
import IconCheck from 'design-system/icons/IconCheck';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { SettingsItemNavigate } from 'design-system/SettingsItem';
import Squircle from 'design-system/Squircle';
import StatusBar from 'design-system/StatusBar';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import useDismissDuplicateGroup from '../hooks/useDismissDuplicateGroup';
import useDuplicateReviewStep from '../hooks/useDuplicateReviewStep';
import { duplicateReviewStrings } from '../utils/duplicateReview';
import { isWeb } from '../../../constants';
// Pairs use the design's copy; groups of three or more get wording that
// doesn't assume a single subscription.
const getReasons = (count) => {
    const many = count > 2;
    return [
        {
            reason: 'someone_else',
            title: many ? 'Some are for someone else' : 'One is for someone else',
            description: 'A partner, family member, or shared plan',
        },
        {
            reason: 'different_things',
            title: 'They’re for different things',
            description: `${many ? 'Different' : 'Two different'} accounts or plans from the same provider`,
        },
        {
            reason: 'not_a_subscription',
            title: many
                ? 'Some aren’t really subscriptions'
                : 'One isn’t really a subscription',
        },
        {
            reason: 'already_cancelled',
            title: duplicateReviewStrings.alreadyCancelled(count),
        },
        { reason: 'something_else', title: 'Something else' },
    ];
};
const strings = {
    pageTitle: 'Keep subscriptions',
    title: (count) => count > 2
        ? `Got it, keeping all. Why do you have ${count}?`
        : 'Got it, keeping both. Why do you have two?',
    subTitle: (count) => `${count > 2 ? 'All of them stay' : 'Both subscriptions stay'} exactly as they are. This just helps us stop asking about things that are fine`,
    successTitle: 'Got it, thanks!',
    successSubTitle: 'We’ve remembered this for next time',
    done: 'Done',
    continue: 'Continue',
};
const DuplicateKeepReasonsScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const { group, step, total, hasNextStep, goToNextStep } = useDuplicateReviewStep(navigation, route.params);
    const colors = useColors();
    const styles = useStyles(styleSet);
    const dismissGroup = useDismissDuplicateGroup();
    const [savingReason, setSavingReason] = useState();
    const onSelectReason = useCallback(async (reason) => {
        if (!group || savingReason)
            return;
        // Picking which one was cancelled happens on its own screen, which
        // also deactivates it.
        if (reason === 'already_cancelled') {
            navigation.navigate('DuplicateCancelled', route.params);
            return;
        }
        setSavingReason(reason);
        const dismissed = await dismissGroup(group, reason);
        setSavingReason(undefined);
        if (!dismissed)
            return;
        navigation.navigateDeprecated('Success', {
            title: strings.successTitle,
            subTitle: strings.successSubTitle,
            contentContainerStyle: styles.content,
            icon: (<Squircle width={rem(88)} height={rem(88)} color={colors.elements.statusPositiveLight}>
                <IconCheck thick width={rem(54)} height={rem(54)} color={colors.elements.statusPositive}/>
              </Squircle>),
            buttonText: hasNextStep ? strings.continue : strings.done,
            onContinue: goToNextStep,
        });
    }, [
        group,
        savingReason,
        dismissGroup,
        styles.content,
        colors.elements.statusPositiveLight,
        colors.elements.statusPositive,
        hasNextStep,
        goToNextStep,
        navigation,
        route.params,
    ]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    if (!group)
        return null;
    const count = group.length;
    return (<AppView withFrame={false}>
          <Animated.ScrollView scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} onScroll={scrollHandler} contentContainerStyle={paddingHorizontalStyle}>
            <StatusBar barStyle="dark-content" animated/>
            <ScreenInfoHeader preTitle={duplicateReviewStrings.progress(step, total)} title={strings.title(count)} subTitle={strings.subTitle(count)}/>
            <CardView noMarginTop webPadding>
              {getReasons(count).map(({ reason, title, description }) => (<View key={reason} style={savingReason === reason ? styles.saving : undefined}>
                  <SettingsItemNavigate titleFirst title={title} description={description} onPress={() => onSelectReason(reason)}/>
                </View>))}
            </CardView>
          </Animated.ScrollView>
        </AppView>);
}, ({ colors }) => ({
    title: '',
    pageTitle: strings.pageTitle,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styleSet = createStyleSheets((colors) => ({
    saving: {
        backgroundColor: colors.background.underlay,
    },
    content: {
        marginBottom: rem(60),
    },
}));
export default DuplicateKeepReasonsScreen;
